/**
 * ProcessingProgress — estado de procesamiento y controles de acción
 *
 * @remarks
 * Muestra el estado de la sesión, el botón de trigger y el de refresh.
 * Visible para todos los estados excepto draft.
 *
 * @module survey/components
 */

import { useState } from 'react';
import { getSurveyApiError, surveyApi } from '../services/survey-api.service';
import type { SurveySession } from '../types/survey.types';
import { SessionStatusBadge } from './SessionStatusBadge';

interface Props {
  session: SurveySession;
  onRefresh: () => Promise<void>;
  onTriggered: () => void;
}

const STATUS_DESCRIPTION: Record<string, string> = {
  draft: 'Sesión lista para recibir archivos.',
  uploading: 'Archivos recibidos. Podés disparar el procesamiento.',
  processing: 'Procesando recorrido. Esto puede tardar varios minutos.',
  processed: 'Procesamiento completado. Capturas disponibles.',
  failed: 'El procesamiento falló. Revisá los archivos y volvé a intentarlo.',
  archived: 'Sesión archivada.',
};

export function ProcessingProgress({ session, onRefresh, onTriggered }: Props): JSX.Element {
  const [isTriggering, setIsTriggering] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  async function handleTrigger(): Promise<void> {
    setIsTriggering(true);
    setTriggerError(null);
    try {
      await surveyApi.triggerProcessing(session.id);
      onTriggered();
    } catch (err) {
      setTriggerError(getSurveyApiError(err));
    } finally {
      setIsTriggering(false);
    }
  }

  async function handleRefresh(): Promise<void> {
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  }

  const canTrigger = session.status === 'uploading' || session.status === 'failed';
  const description = STATUS_DESCRIPTION[session.status] ?? '';

  return (
    <div className="sw-processing">
      <div className="sw-processing-status">
        <SessionStatusBadge status={session.status} />
        <p className="sw-processing-description">{description}</p>
      </div>

      {triggerError && <p className="sw-processing-error">{triggerError}</p>}

      <div className="sw-processing-actions">
        {canTrigger && (
          <button
            type="button"
            className="sw-btn sw-btn-primary"
            disabled={isTriggering}
            onClick={() => void handleTrigger()}
          >
            {isTriggering ? 'Disparando...' : 'Procesar sesión'}
          </button>
        )}

        <button
          type="button"
          className="sw-btn sw-btn-ghost"
          disabled={isRefreshing}
          onClick={() => void handleRefresh()}
        >
          {isRefreshing ? 'Actualizando...' : 'Refrescar estado'}
        </button>
      </div>

      {session.status === 'processed' && (
        <p className="sw-processing-hint">
          Las capturas están disponibles. El visor estará disponible en la siguiente fase.
        </p>
      )}
    </div>
  );
}
